import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, Divider, Typography } from '@mui/material';
import searchIcon from 'assets/images/search-icon.svg';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { LocationType, UserSearch } from 'types';
import { useBranchContext } from 'context/BranchesContext';
import { useUserSearchContext } from 'context/UserSearchContext';
import { Loader } from 'components';
import { branchHoursToGetReady } from 'services/branchService';
import { differenceInCalendarDays, format } from 'date-fns';
import { setUserSearch } from 'utils/localStorage';
import { toast } from 'react-toastify';
import { Address } from 'types/Location';
import { AxiosError } from 'axios';
import { useBranches, useBranchHoursToGetReady } from 'hooks/useBranches';

type BranchTypeProps = 'out' | 'in';
export const Location: React.FC = () => {
  const { allBranches: branches } = useBranchContext();

  interface RouteParams extends Record<string, string | undefined> {
    locationTypeParams: BranchTypeProps;
  }
  const { locationTypeParams } = useParams<RouteParams>();
  const [hasUpdatedInDetails, setHasUpdatedInDetails] = useState(false);
  const [outBranchId, setOutBranchId] = useState<string>();
  const [inBranchId, setDropoffBranchId] = useState<string>();
  const [searchText, setSearchText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const {
    bookingSearchData,
    setBookingSearchData,
    loading: bookingSearchDataLoading,
  } = useUserSearchContext();

  const [userInput, setUserInput] = useState<{
    fieldName: string;
    value: string;
  }>({
    fieldName: '',
    value: '',
  });

  const activeBranchId =
    locationTypeParams === 'out' ? outBranchId : inBranchId;

  const {
    data: branchHours,
    isLoading: branchHoursIsLoading,
    error: branchHoursError,
  } = useBranchHoursToGetReady(activeBranchId!);

  const locationNameKey =
    locationTypeParams === 'out' ? 'pickupLocationName' : 'returnLocationName';

  const locationKey: string =
    locationTypeParams === 'out' ? 'pickup' : 'return';

  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSearchText(value);
  };

  const filteredData = useMemo(() => {
    if (!searchText.trim()) return [...branches]; // show all until user types
    return branches.filter((b) =>
      b.Name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [searchText, branches]);

  const locationSelection = async (
    fieldName: string | undefined,
    selectedData: LocationType
  ) => {
    if (!fieldName || !selectedData.Id) {
      return;
    }

    const baseLocation: Address = {
      Date: '',
      Time: '',
      BranchId: selectedData.Id,
      BranchCode: selectedData.Code,
      StateCode: selectedData.StateCode,
      IsDelOrCol: false,
      BranchName: selectedData.Name,
      AreaName: selectedData.Address,
      Address: selectedData.Address,
      StateName: selectedData.StateName,
    };
    if (locationTypeParams === 'out') {
      setBookingSearchData((prev) => ({
        ...prev,
        OutDetails: { ...baseLocation },
        InDetails: { ...baseLocation },
        branchType: 'out',
      }));
      setOutBranchId(selectedData.Id);
      return;
    }

    setBookingSearchData((prev) => ({
      ...prev,
      InDetails: { ...baseLocation },
      branchType: 'in',
    }));
    setDropoffBranchId(selectedData.Id);
    return;
  };

  // Sync user input with bookingSearchData changes
  useEffect(() => {
    const branchName =
      locationKey === 'pickup'
        ? bookingSearchData.OutDetails.BranchName
        : bookingSearchData.InDetails.BranchName;

    setUserInput({
      fieldName: locationNameKey,
      value: branchName,
    });
  }, [locationKey, locationNameKey]);

  useEffect(() => {
    if (branchHours?.result) {
      const { OutDate, OutTime, InDate, InTime } = branchHours.result;

      const startDateTime = new Date(OutDate);
      const endDateTime = new Date(InDate);
      const diffDays = differenceInCalendarDays(endDateTime, startDateTime);

      if (locationTypeParams === 'out') {
        setBookingSearchData((prev) => ({
          ...prev,
          OutDetails: {
            ...prev.OutDetails,
            Date: format(OutDate, 'dd MMM yyyy'),
            Time: OutTime,
          },
          InDetails: {
            ...prev.InDetails,
            Date: format(InDate, 'dd MMM yyyy'),
            Time: InTime,
          },
          numberOfDays: diffDays,
          isPickupField: false,
          activeBranchId: '',
          branchType: 'out',
        }));
      }

      if (locationTypeParams === 'in') {
        setBookingSearchData((prev) => ({
          ...prev,
          InDetails: {
            ...prev.InDetails,
            Date: format(InDate, 'dd MMM yyyy'),
            Time: InTime,
          },
          branchType: 'in',
        }));
      }

      setHasUpdatedInDetails(true);
    }
  }, [branchHours, locationTypeParams]);

  useEffect(() => {
    if (hasUpdatedInDetails && !branchHoursIsLoading) {
      setHasUpdatedInDetails(false); // reset the flag
      navigate(-1);
    }
  }, [hasUpdatedInDetails, branchHoursIsLoading]);

  if (loading || branchHoursIsLoading || bookingSearchDataLoading)
    return <Loader />;
  if (error || branchHoursError) toast.error(error || branchHoursError.message);
  const branchesData: LocationType[] =
    filteredData.length > 0 ? [...filteredData] : [...branches];

  console.log(filteredData);

  return (
    <>
      <Box className="searchWrapper">
        <Box className="searchInputGroup">
          <img
            src={searchIcon}
            className="searchIcon"
            alt=""
            aria-hidden="true"
          />
          <input
            id="search-input"
            type="search"
            className="searchInput"
            value={searchText}
            name={locationTypeParams}
            onChange={handleInputChange}
            placeholder="Search"
            aria-label="Search input"
          />
        </Box>
      </Box>
      <Box className="listItems" sx={{ marginTop: '85px' }}>
        {branchesData &&
          branchesData.map((item, index) => {
            return (
              <Box
                className="optionWrapper"
                key={index}
                sx={{ margin: '10px auto' }}
                onClick={() => locationSelection(locationTypeParams, item)}
              >
                <Box
                  className="searchInputGroup"
                  sx={{ justifyContent: 'space-between' }}
                >
                  <Typography component={'p'}>{item.Name}</Typography>
                  <ArrowForwardIosIcon />
                </Box>
              </Box>
            );
          })}
      </Box>
    </>
  );
};
